import type { CalculatorId } from '../lib/calculations';

export type FieldDefinition = {
  key: string;
  label: string;
  type?: 'number' | 'select';
  defaultValue: number;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  help?: string;
  options?: { label: string; value: number }[];
};

export type CalculatorDefinition = {
  id: CalculatorId;
  title: string;
  category: 'Solar' | 'Electricity';
  path: string;
  description: string;
  updated: string;
  fields: FieldDefinition[];
  reviewer: string;
};

const num = (
  key: string,
  label: string,
  defaultValue: number,
  suffix?: string,
  extra: Partial<FieldDefinition> = {},
): FieldDefinition => ({
  key,
  label,
  defaultValue,
  type: 'number',
  suffix,
  min: 0,
  step: 0.01,
  ...extra,
});

const select = (
  key: string,
  label: string,
  defaultValue: number,
  options: FieldDefinition['options'],
): FieldDefinition => ({ key, label, defaultValue, type: 'select', options });

export const calculatorDefinitions: CalculatorDefinition[] = [
  {
    id: 'electricity-bill',
    title: 'Electricity bill calculator',
    category: 'Electricity',
    path: '/electricity/calculators/electricity-bill',
    description: 'Estimate a Philippine electricity bill from meter readings and charges.',
    updated: '2026-07-17',
    reviewer: 'Energy Methodology Team',
    fields: [
      num('previous', 'Previous meter reading', 12450, 'kWh'),
      num('current', 'Current meter reading', 12790, 'kWh'),
      num('rate', 'Electricity rate', 11.5, 'PHP/kWh'),
      num('fixedFees', 'Fixed fees', 250, 'PHP'),
      num('otherCharges', 'Other charges', 0, 'PHP'),
      num('days', 'Billing duration', 30, 'days', { min: 1, max: 60 }),
    ],
  },
  {
    id: 'appliance-cost',
    title: 'Appliance electricity cost',
    category: 'Electricity',
    path: '/electricity/calculators/appliance-cost',
    description: 'Calculate daily, monthly, and annual appliance running costs.',
    updated: '2026-07-17',
    reviewer: 'Energy Methodology Team',
    fields: [
      num('wattage', 'Appliance wattage', 1000, 'W'),
      num('quantity', 'Quantity', 1),
      num('hours', 'Hours per day', 4, 'hours'),
      num('days', 'Days per month', 30, 'days'),
      num('rate', 'Electricity rate', 11.5, 'PHP/kWh'),
    ],
  },
  {
    id: 'aircon-cost',
    title: 'Air-conditioner cost calculator',
    category: 'Electricity',
    path: '/electricity/calculators/aircon-cost',
    description: 'Estimate air-conditioner energy use under practical usage scenarios.',
    updated: '2026-07-17',
    reviewer: 'Energy Methodology Team',
    fields: [
      num('horsepower', 'Air-conditioner size', 1.5, 'HP'),
      select('inverter', 'Unit type', 1, [
        { label: 'Inverter', value: 1 },
        { label: 'Non-inverter', value: 2 },
      ]),
      num('powerDraw', 'Rated power draw', 1200, 'W'),
      num('hours', 'Hours per day', 8, 'hours'),
      num('roomSize', 'Room size', 20, 'm²'),
      num('rate', 'Electricity rate', 11.5, 'PHP/kWh'),
    ],
  },
  {
    id: 'solar-savings',
    title: 'Solar savings calculator',
    category: 'Solar',
    path: '/solar/calculators/solar-savings',
    description:
      'Estimate system size, installation range, savings, and payback for a Philippine property.',
    updated: '2026-07-17',
    reviewer: 'Solar Methodology Team',
    fields: [
      num('monthlyBill', 'Average monthly bill', 6000, 'PHP'),
      num('rate', 'Electricity rate', 11.5, 'PHP/kWh'),
      num('shade', 'Roof shading', 10, '%', { max: 60 }),
      num('daytimeUse', 'Daytime electricity use', 65, '%', { max: 100 }),
      num('targetReduction', 'Target bill reduction', 70, '%', { max: 100 }),
      select('systemType', 'System type', 1, [
        { label: 'Grid-tied', value: 1 },
        { label: 'Hybrid', value: 2 },
        { label: 'Off-grid', value: 3 },
      ]),
      num('panelWattage', 'Panel wattage', 550, 'W'),
      num('rateGrowth', 'Annual electricity-rate growth', 4, '%'),
      num('degradation', 'Annual panel degradation', 0.5, '%'),
      num('maintenance', 'Annual maintenance allowance', 1, '%'),
    ],
  },
  {
    id: 'system-size',
    title: 'Solar system size calculator',
    category: 'Solar',
    path: '/solar/calculators/system-size',
    description: 'Estimate recommended kW, panel count, generation, roof area, and cost.',
    updated: '2026-07-17',
    reviewer: 'Solar Methodology Team',
    fields: [
      num('monthlyKwh', 'Monthly electricity use', 450, 'kWh'),
      num('targetReduction', 'Target offset', 80, '%', { max: 100 }),
      num('panelWattage', 'Panel wattage', 550, 'W'),
    ],
  },
  {
    id: 'payback-roi',
    title: 'Solar payback and ROI',
    category: 'Solar',
    path: '/solar/calculators/payback-roi',
    description: 'Compare cash and financing-adjusted solar payback.',
    updated: '2026-07-17',
    reviewer: 'Solar Methodology Team',
    fields: [
      num('systemCost', 'Installed system cost', 350000, 'PHP'),
      num('annualSavings', 'Expected annual savings', 65000, 'PHP'),
      num('annualMaintenance', 'Annual maintenance', 3500, 'PHP'),
      num('financingRate', 'Financing rate', 0, '%'),
      num('financingYears', 'Financing term', 5, 'years'),
    ],
  },
  {
    id: 'battery-size',
    title: 'Solar battery size calculator',
    category: 'Solar',
    path: '/solar/calculators/battery-size',
    description:
      'Estimate usable and nominal battery capacity, inverter size, runtime, and budget.',
    updated: '2026-07-17',
    reviewer: 'Solar Methodology Team',
    fields: [
      num('loadWatts', 'Backup load', 1500, 'W'),
      num('runtime', 'Target runtime', 6, 'hours'),
      num('surgeMultiplier', 'Surge multiplier', 2),
      select('chemistry', 'Battery chemistry', 1, [
        { label: 'LiFePO₄', value: 1 },
        { label: 'Lead-acid', value: 2 },
      ]),
      num('depthOfDischarge', 'Depth of discharge', 80, '%', { max: 95 }),
    ],
  },
  {
    id: 'panel-count-roof-area',
    title: 'Panel count and roof area',
    category: 'Solar',
    path: '/solar/calculators/panel-count-roof-area',
    description: 'Check how many panels fit and whether the roof is large enough.',
    updated: '2026-07-17',
    reviewer: 'Solar Methodology Team',
    fields: [
      num('systemKw', 'Target system size', 5, 'kW'),
      num('panelWattage', 'Panel wattage', 550, 'W'),
      num('panelLength', 'Panel length', 2.28, 'm'),
      num('panelWidth', 'Panel width', 1.13, 'm'),
      num('roofLength', 'Roof length', 8, 'm'),
      num('roofWidth', 'Roof width', 7, 'm'),
      num('obstructions', 'Obstruction allowance', 15, '%', { max: 60 }),
    ],
  },
];

export const guides = [
  [
    'solar-panel-cost-philippines',
    'Solar panel cost in the Philippines',
    'Understand what really changes the price of a residential solar proposal, and how to compare quotations fairly.',
  ],
  [
    'how-many-solar-panels',
    'How many panels a Philippine home needs',
    'Translate electricity use, target savings, and roof limits into a practical panel-count estimate.',
  ],
  [
    'grid-tied-vs-hybrid',
    'Grid-tied versus hybrid solar',
    'Compare lower-cost daytime savings with higher-cost backup-ready systems.',
  ],
  [
    'solar-during-brownouts',
    'Does solar work during brownouts?',
    'Learn why many solar systems shut down during outages and what is required for backup power.',
  ],
  [
    'solar-payback-period',
    'Solar payback period explained',
    'Understand what payback really measures and why simplistic savings claims can mislead buyers.',
  ],
  [
    'choosing-solar-installer',
    'Choosing a solar installer',
    'A practical due-diligence guide for shortlisting and comparing installers.',
  ],
  [
    'inverter-sizing',
    'Solar inverter sizing',
    'Understand inverter range, DC-to-AC ratio, clipping, and backup constraints.',
  ],
  [
    'battery-sizing',
    'Battery sizing for backup',
    'Estimate runtime, usable capacity, and the real limits of residential battery backup.',
  ],
  [
    'roof-area-requirements',
    'Solar roof-area requirements',
    'Check how roof size, shape, and shading affect how much solar you can fit.',
  ],
  [
    'understanding-electricity-bill',
    'Understanding an electricity bill',
    'Read your bill properly before using it to justify solar or appliance decisions.',
  ],
  [
    'high-consumption-appliances',
    'High-consumption appliances',
    'Identify which household appliances are most likely driving your electricity cost.',
  ],
  [
    'aircon-electricity-costs',
    'Air-conditioner electricity costs',
    'Estimate realistic cooling costs and avoid common assumptions about inverter savings.',
  ],
  [
    'solar-for-farms',
    'Solar for farms',
    'Think through pumps, ventilation, lighting, and cold loads before buying farm solar.',
  ],
  [
    'solar-water-pumps',
    'Solar water pumps',
    'Plan pump solar using water volume, head, runtime, and system efficiency.',
  ],
  [
    'cash-vs-financing',
    'Cash versus solar financing',
    'Compare lower upfront cost with financing-adjusted total project cost.',
  ],
  [
    'solar-installation-mistakes',
    'Common solar installation mistakes',
    'Avoid the failures that often turn a promising system into a frustrating one.',
  ],
  [
    'solar-maintenance-costs',
    'Solar maintenance costs',
    'Budget realistically for cleaning, inspections, inverter replacement, and long-term care.',
  ],
  [
    'panel-lifespan-degradation',
    'Panel lifespan and degradation',
    'Understand long-term output decline without believing unrealistic “forever” claims.',
  ],
  [
    'solar-quotation-checklist',
    'Solar quotation checklist',
    'Normalize quotations so you compare complete scope instead of catchy prices.',
  ],
  [
    'questions-for-installers',
    'Questions to ask solar installers',
    'Use a disciplined question list before you sign or pay a reservation fee.',
  ],
  [
    'net-metering-philippines',
    'Net metering in the Philippines',
    'Understand what net metering can and cannot do for residential solar economics.',
  ],
  [
    'when-solar-not-worth-it',
    'When solar may not be worth it',
    'Recognize the situations where delaying solar is the better decision.',
  ],
  [
    'roof-orientation-and-shading',
    'Roof orientation and shading',
    'See how direction, obstructions, and shade timing affect practical solar output.',
  ],
  [
    'solar-for-small-enterprises',
    'Solar for small businesses',
    'Evaluate daytime loads, payback, and operational fit for commercial solar.',
  ],
  [
    'backup-load-prioritization',
    'Backup load prioritization',
    'Decide what should stay on during outages before you overspend on batteries.',
  ],
  [
    'solar-loan-red-flags',
    'Solar loan red flags',
    'Spot financing terms that can quietly make a solar deal much worse.',
  ],
  [
    'off-grid-solar-basics',
    'Off-grid solar basics',
    'Learn when off-grid solar makes sense and why it demands more discipline.',
  ],
  [
    'solar-permits-philippines',
    'Solar permits and utility steps in the Philippines',
    'Know the documents, approvals, and expectations before installation begins.',
  ],
  [
    'solar-monitoring-basics',
    'Solar monitoring basics',
    'Use monitoring data properly instead of only checking the app when something feels wrong.',
  ],
  [
    'panel-brand-vs-installer-quality',
    'Panel brand versus installer quality',
    'Know when workmanship and design matter more than chasing famous brands.',
  ],
  [
    'battery-replacement-planning',
    'Battery replacement planning',
    'Budget for the part of hybrid ownership that many sales pitches skip.',
  ],
  [
    'solar-proposal-red-flags',
    'Solar proposal red flags',
    'Identify warning signs before a vague or overhyped proposal costs you money.',
  ],
  [
    'solar-roof-condition-check',
    'Checking roof condition before solar',
    'Make sure your roof is ready before you install a system meant to stay for years.',
  ],
  [
    'solar-insurance-and-warranties',
    'Solar insurance and warranties',
    'Understand what protection actually exists after installation.',
  ],
  [
    'battery-vs-generator',
    'Battery versus generator backup',
    'Compare silence and convenience with fuel flexibility and lower upfront cost.',
  ],
] as const;

export const categories = ['Solar', 'Electricity'] as const;
export const getCalculator = (id: string) => calculatorDefinitions.find((item) => item.id === id);
export const getGuide = (slug: string) => guides.find(([guideSlug]) => guideSlug === slug);
